import {
  type EquipmentDetails,
  type FileItem,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  TICKET_SOURCE_LABELS,
  TICKET_SOURCES,
  type TicketDetails,
  type TicketPriority,
  type TicketSource,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Dialog } from '@/components/dialog';
import { FileUpload } from '@/components/file-upload';
import { Button, Field, Input, Select, Textarea } from '@/components/ui';
import { UserPicker } from '@/components/user-picker';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { equipmentMetaQuery } from '@/lib/equipment';
import { todayIso } from '@/lib/format';
import { ClientPicker, EquipmentPicker } from './equipment-bits';

/** Клиент в форме: контакт (с его компанией) или только компания. */
export interface ClientValue {
  kind: 'contact' | 'company';
  id: string;
  label: string;
  companyId: string | null;
}

export function clientOf(x: {
  contact: { id: string; fullName: string } | null;
  company: { id: string; name: string } | null;
}): ClientValue | null {
  if (x.contact)
    return {
      kind: 'contact',
      id: x.contact.id,
      label: x.company ? `${x.contact.fullName} · ${x.company.name}` : x.contact.fullName,
      companyId: x.company?.id ?? null,
    };
  if (x.company) return { kind: 'company', id: x.company.id, label: x.company.name, companyId: x.company.id };
  return null;
}

const clientBody = (c: ClientValue | null) => ({
  contactId: c?.kind === 'contact' ? c.id : null,
  companyId: c?.companyId ?? null,
});

function useSave<T>(
  fn: (body: Record<string, unknown>) => Promise<T>,
  onDone: (res: T) => void | Promise<void>,
) {
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<ReactNode>(null);
  const m = useMutation({
    mutationFn: fn,
    onSuccess: async (res) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['equipment'] }),
        qc.invalidateQueries({ queryKey: ['helpdesk'] }),
      ]);
      await onDone(res);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length && !err.fields._ ? null : (err.fields._ ?? err.message));
      } else setFormError(err.message);
    },
  });
  return { m, errors, setErrors, formError };
}

function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
    >
      {children}
    </p>
  );
}

function Footer({
  onClose,
  loading,
  label = 'Сохранить',
}: {
  onClose: () => void;
  loading: boolean;
  label?: string;
}) {
  return (
    <div className="flex justify-end gap-2 border-t border-line pt-4">
      <Button variant="secondary" onClick={onClose}>
        Отмена
      </Button>
      <Button type="submit" loading={loading}>
        {label}
      </Button>
    </div>
  );
}

const str = (fd: FormData, key: string) => String(fd.get(key) ?? '').trim();
const plusYear = (iso: string) => `${Number(iso.slice(0, 4)) + 1}${iso.slice(4)}`;

// ---------- Оборудование ----------

/**
 * Новый стенд в производство, внесение работающего у клиента оборудования или правка карточки.
 * `defaults` — запуск из сделки: клиент и сделка уже известны.
 */
export function EquipmentFormDialog({
  equipment,
  mode = 'production',
  defaults,
  onClose,
}: {
  equipment?: EquipmentDetails;
  mode?: 'production' | 'in_service';
  defaults?: { client?: ClientValue | null; dealId?: string; dealLabel?: string };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const meta = useQuery(equipmentMetaQuery);
  const nextSerial = useQuery({
    queryKey: ['equipment', 'next-serial'],
    queryFn: () => api<{ serialNo: string }>('/equipment/next-serial'),
    enabled: !equipment,
  });
  const lifecycle = equipment?.lifecycle ?? mode;
  const production = lifecycle === 'production';
  const [client, setClient] = useState<ClientValue | null>(
    equipment ? clientOf(equipment) : (defaults?.client ?? null),
  );
  const [assembler, setAssembler] = useState<string[]>(
    equipment?.assembler ? [equipment.assembler.userId] : [],
  );
  const [contract, setContract] = useState<FileItem[]>(
    equipment?.contractFile ? [equipment.contractFile] : [],
  );
  const { m, errors, setErrors, formError } = useSave(
    (body) =>
      equipment
        ? api(`/equipment/${equipment.id}`, { method: 'PATCH', body }).then(() => ({ id: equipment.id }))
        : api<{ id: string }>('/equipment', { method: 'POST', body }),
    async (res) => {
      onClose();
      if (!equipment) await navigate({ to: '/equipment/$id', params: { id: res.id } });
    },
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {
      modelId: str(fd, 'modelId') || null,
      name: str(fd, 'name'),
      serialNo: str(fd, 'serialNo'),
      boardId: str(fd, 'boardId'),
      ...clientBody(client),
      contractFileId: contract.at(-1)?.id ?? null,
      notes: str(fd, 'notes'),
    };
    if (!equipment) {
      body.lifecycle = lifecycle;
      if (defaults?.dealId) body.dealId = defaults.dealId;
    }
    if (production) {
      body.assemblerId = assembler[0] ?? null;
      body.caseExpectedAt = str(fd, 'caseExpectedAt');
      body.boardExpectedAt = str(fd, 'boardExpectedAt');
      if (equipment) body.assembledAt = str(fd, 'assembledAt');
    } else {
      body.shippedAt = str(fd, 'shippedAt');
      body.warrantyUntil = str(fd, 'warrantyUntil');
      body.location = str(fd, 'location');
      body.inventoryNo = str(fd, 'inventoryNo');
    }
    if (!body.modelId && !body.name) return setErrors({ modelId: 'Выберите модель или впишите название' });
    if (!production && !client) return setErrors({ client: 'Чьё это оборудование' });
    if (equipment && !body.serialNo) delete body.serialNo;
    m.mutate(body);
  }

  const models = meta.data?.models.filter((x) => x.isActive || x.id === equipment?.model?.id) ?? [];
  const title = equipment
    ? equipment.title
    : production
      ? 'Новый стенд в производство'
      : 'Оборудование клиента в базу';

  return (
    <Dialog title={title} onClose={onClose} width={680}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        {defaults?.dealLabel && <p className="text-[13.5px] text-steel">По сделке {defaults.dealLabel}</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Модель" error={errors.modelId}>
            {(p) => (
              <Select {...p} name="modelId" defaultValue={equipment?.model?.id ?? models[0]?.id ?? ''}>
                <option value="">Нет в списке</option>
                {models.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Название" error={errors.name} hint="Если модели нет в списке">
            {(p) => (
              <Input
                {...p}
                name="name"
                defaultValue={equipment?.name ?? ''}
                placeholder="Стенд Bosch EPS 815"
              />
            )}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Заводской №"
            error={errors.serialNo}
            hint={equipment ? undefined : 'Пусто — следующий по порядку'}
          >
            {(p) => (
              <Input
                {...p}
                name="serialNo"
                defaultValue={equipment?.serialNo ?? ''}
                placeholder={nextSerial.data?.serialNo ?? ''}
              />
            )}
          </Field>
          <Field label="ID платы" error={errors.boardId}>
            {(p) => <Input {...p} name="boardId" defaultValue={equipment?.boardId ?? ''} />}
          </Field>
        </div>
        {production ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ожидание корпуса" error={errors.caseExpectedAt} hint="Когда придёт">
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    name="caseExpectedAt"
                    defaultValue={equipment?.caseExpectedAt ?? ''}
                  />
                )}
              </Field>
              <Field label="Ожидание платы" error={errors.boardExpectedAt} hint="Когда придёт">
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    name="boardExpectedAt"
                    defaultValue={equipment?.boardExpectedAt ?? ''}
                  />
                )}
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Сборщик" error={errors.assemblerId}>
                {(p) => <UserPicker id={p.id} value={assembler} onChange={setAssembler} />}
              </Field>
              {equipment && (
                <Field
                  label="Окончание сборки"
                  error={errors.assembledAt}
                  hint="Ставится само на этапе упаковки"
                >
                  {(p) => (
                    <Input {...p} type="date" name="assembledAt" defaultValue={equipment.assembledAt ?? ''} />
                  )}
                </Field>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Дата отгрузки" error={errors.shippedAt}>
                {(p) => (
                  <Input {...p} type="date" name="shippedAt" defaultValue={equipment?.shippedAt ?? ''} />
                )}
              </Field>
              <Field label="Гарантия до" error={errors.warrantyUntil}>
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    name="warrantyUntil"
                    defaultValue={equipment?.warrantyUntil ?? ''}
                  />
                )}
              </Field>
              <Field label="Инв. № клиента" error={errors.inventoryNo}>
                {(p) => <Input {...p} name="inventoryNo" defaultValue={equipment?.inventoryNo ?? ''} />}
              </Field>
            </div>
            <Field label="Где стоит" error={errors.location}>
              {(p) => (
                <Input
                  {...p}
                  name="location"
                  defaultValue={equipment?.location ?? ''}
                  placeholder="Город, адрес мастерской"
                />
              )}
            </Field>
          </>
        )}
        <Field
          label={production ? 'Клиент (если стенд под заказ)' : 'Клиент'}
          error={errors.client ?? errors.contactId ?? errors.companyId}
        >
          {(p) => (
            <ClientPicker
              id={p.id}
              invalid={p['aria-invalid']}
              value={client}
              onChange={(c) =>
                setClient(
                  c
                    ? {
                        kind: c.kind,
                        id: c.id,
                        label:
                          c.companyName && c.kind === 'contact' ? `${c.title} · ${c.companyName}` : c.title,
                        companyId: c.companyId,
                      }
                    : null,
                )
              }
            />
          )}
        </Field>
        <Field label="Договор" error={errors.contractFileId}>
          {(p) => (
            <FileUpload
              id={p.id}
              value={contract}
              onChange={(list) => setContract(list.slice(-1))}
              label={contract.length ? 'Заменить файл' : 'Прикрепить договор'}
            />
          )}
        </Field>
        <Field label="Примечание" error={errors.notes}>
          {(p) => <Textarea {...p} name="notes" rows={2} defaultValue={equipment?.notes ?? ''} />}
        </Field>
        <FormError>{formError}</FormError>
        <Footer
          onClose={onClose}
          loading={m.isPending}
          label={equipment ? 'Сохранить' : production ? 'Запустить' : 'Внести в базу'}
        />
      </form>
    </Dialog>
  );
}

/** Отгрузка клиенту: стенд переходит в базу оборудования у клиента. */
export function ShipDialog({ equipment, onClose }: { equipment: EquipmentDetails; onClose: () => void }) {
  const [client, setClient] = useState<ClientValue | null>(clientOf(equipment));
  const today = todayIso();
  const { m, errors, setErrors, formError } = useSave(
    (body) => api(`/equipment/${equipment.id}/ship`, { method: 'POST', body }),
    () => onClose(),
  );
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (!client) return setErrors({ client: 'Кому отгружаем' });
    m.mutate({
      shippedAt: str(fd, 'shippedAt'),
      warrantyUntil: str(fd, 'warrantyUntil'),
      location: str(fd, 'location'),
      ...clientBody(client),
    });
  }
  return (
    <Dialog title={`Отгрузка: ${equipment.title}`} onClose={onClose} width={560}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Клиент" error={errors.client ?? errors.contactId}>
          {(p) => (
            <ClientPicker
              id={p.id}
              invalid={p['aria-invalid']}
              value={client}
              onChange={(c) =>
                setClient(
                  c
                    ? {
                        kind: c.kind,
                        id: c.id,
                        label:
                          c.companyName && c.kind === 'contact' ? `${c.title} · ${c.companyName}` : c.title,
                        companyId: c.companyId,
                      }
                    : null,
                )
              }
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Дата отгрузки" error={errors.shippedAt}>
            {(p) => <Input {...p} type="date" name="shippedAt" defaultValue={today} />}
          </Field>
          <Field label="Гарантия до" error={errors.warrantyUntil}>
            {(p) => (
              <Input
                {...p}
                type="date"
                name="warrantyUntil"
                defaultValue={equipment.warrantyUntil ?? plusYear(today)}
              />
            )}
          </Field>
        </div>
        <Field label="Где будет стоять" error={errors.location}>
          {(p) => (
            <Input
              {...p}
              name="location"
              defaultValue={equipment.location ?? ''}
              placeholder="Город, адрес"
            />
          )}
        </Field>
        <FormError>{formError}</FormError>
        <Footer onClose={onClose} loading={m.isPending} label="Отгрузить" />
      </form>
    </Dialog>
  );
}

/** Короткая форма «одно поле с текстом»: причина списания, итог заявки, причина отмены. */
export function ReasonDialog({
  title,
  label,
  placeholder,
  submitLabel,
  loading,
  error,
  onSubmit,
  onClose,
}: {
  title: string;
  label: string;
  placeholder?: string;
  submitLabel: string;
  loading: boolean;
  error?: string | null;
  onSubmit: (text: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [empty, setEmpty] = useState(false);
  return (
    <Dialog title={title} onClose={onClose} width={520}>
      <form
        noValidate
        className="flex flex-col gap-4 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!text.trim()) return setEmpty(true);
          onSubmit(text.trim());
        }}
      >
        <Field label={label} error={empty ? 'Обязательное поле' : undefined}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={text}
              placeholder={placeholder}
              onChange={(e) => {
                setText(e.target.value);
                setEmpty(false);
              }}
            />
          )}
        </Field>
        <FormError>{error}</FormError>
        <Footer onClose={onClose} loading={loading} label={submitLabel} />
      </form>
    </Dialog>
  );
}

// ---------- Сервисные заявки ----------

export interface TicketDefaults {
  equipment?: { id: string; label: string } | null;
  client?: ClientValue | null;
  dealId?: string;
  chatId?: string;
  subject?: string;
  description?: string;
  source?: TicketSource;
}

export function TicketFormDialog({
  ticket,
  defaults,
  onClose,
}: {
  ticket?: TicketDetails;
  defaults?: TicketDefaults;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const can = useCan();
  const meta = useQuery(equipmentMetaQuery);
  const worker = can('helpdesk.access');
  const canLink = can('equipment.read') || worker || can('production.manage') || can('crm.access');
  const canPickEquipment = can('equipment.read') || worker || can('production.manage');
  const [equipment, setEquipment] = useState<{ id: string; label: string } | null>(
    ticket?.equipment
      ? { id: ticket.equipment.id, label: ticket.equipment.title }
      : (defaults?.equipment ?? null),
  );
  const [client, setClient] = useState<ClientValue | null>(
    ticket ? clientOf(ticket) : (defaults?.client ?? null),
  );
  const [assignee, setAssignee] = useState<string[]>(ticket?.assignee ? [ticket.assignee.userId] : []);
  const [priority, setPriority] = useState<TicketPriority>(ticket?.priority ?? 'normal');
  const { m, errors, setErrors, formError } = useSave(
    (body) =>
      ticket
        ? api(`/helpdesk/tickets/${ticket.id}`, { method: 'PATCH', body }).then(() => ({ id: ticket.id }))
        : api<{ id: string }>('/helpdesk/tickets', { method: 'POST', body }),
    async (res) => {
      onClose();
      if (!ticket) await navigate({ to: '/helpdesk/$id', params: { id: res.id } });
    },
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {
      subject: str(fd, 'subject'),
      description: str(fd, 'description'),
      priority: fd.get('priority') as TicketPriority,
      categoryId: str(fd, 'categoryId') || null,
    };
    if (!body.subject) return setErrors({ subject: 'Коротко: что случилось' });
    if (canLink) {
      Object.assign(body, clientBody(client));
      if (canPickEquipment) body.equipmentId = equipment?.id ?? null;
    }
    if (fd.get('source')) body.source = fd.get('source');
    if (worker) body.assigneeId = assignee[0] ?? null;
    if (!ticket) {
      if (defaults?.dealId) body.dealId = defaults.dealId;
      if (defaults?.chatId) body.chatId = defaults.chatId;
    }
    m.mutate(body);
  }

  const categories = meta.data?.categories.filter((c) => c.isActive || c.id === ticket?.category?.id) ?? [];
  const rule = meta.data?.sla.find((r) => r.priority === priority);

  return (
    <Dialog
      title={ticket ? `Заявка #${ticket.number}` : 'Новая сервисная заявка'}
      onClose={onClose}
      width={680}
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Что случилось" error={errors.subject}>
          {(p) => (
            <Input
              {...p}
              name="subject"
              defaultValue={ticket?.subject ?? defaults?.subject ?? ''}
              placeholder="Например: стенд не держит давление"
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Приоритет"
            error={errors.priority}
            hint={rule ? `Взять за ${rule.reactionHours} ч, решить за ${rule.resolveHours} ч` : undefined}
          >
            {(p) => (
              <Select
                {...p}
                name="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
              >
                {TICKET_PRIORITIES.map((x) => (
                  <option key={x} value={x}>
                    {TICKET_PRIORITY_LABELS[x]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Категория" error={errors.categoryId}>
            {(p) => (
              <Select {...p} name="categoryId" defaultValue={ticket?.category?.id ?? ''}>
                <option value="">Не выбрана</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {!defaults?.chatId && (
            <Field label="Источник" error={errors.source}>
              {(p) => (
                <Select {...p} name="source" defaultValue={ticket?.source ?? defaults?.source ?? 'phone'}>
                  {TICKET_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {TICKET_SOURCE_LABELS[s]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>
        {canLink && (
          <div className="grid gap-4 sm:grid-cols-2">
            {canPickEquipment && (
              <Field label="Оборудование" error={errors.equipmentId} hint="Клиент подставится сам">
                {(p) => (
                  <EquipmentPicker
                    id={p.id}
                    value={equipment}
                    onChange={(x) => {
                      setEquipment(x ? { id: x.id, label: x.title } : null);
                      if (x && !client) setClient(clientOf(x));
                    }}
                  />
                )}
              </Field>
            )}
            <Field
              label="Клиент"
              error={errors.contactId ?? errors.companyId}
              hint={defaults?.chatId && !client ? 'Из чата' : undefined}
            >
              {(p) => (
                <ClientPicker
                  id={p.id}
                  value={client}
                  onChange={(c) =>
                    setClient(
                      c
                        ? {
                            kind: c.kind,
                            id: c.id,
                            label:
                              c.companyName && c.kind === 'contact'
                                ? `${c.title} · ${c.companyName}`
                                : c.title,
                            companyId: c.companyId,
                          }
                        : null,
                    )
                  }
                />
              )}
            </Field>
          </div>
        )}
        <Field label="Подробности" error={errors.description}>
          {(p) => (
            <Textarea
              {...p}
              name="description"
              rows={4}
              defaultValue={ticket?.description ?? defaults?.description ?? ''}
              placeholder={
                canLink
                  ? 'Симптомы, ошибки, что уже пробовали'
                  : 'Клиент, оборудование, что случилось, как связаться'
              }
            />
          )}
        </Field>
        {worker && (
          <Field
            label="Исполнитель"
            error={errors.assigneeId}
            hint={assignee.length ? undefined : 'Пусто — увидит весь сервис'}
          >
            {(p) => <UserPicker id={p.id} value={assignee} onChange={setAssignee} />}
          </Field>
        )}
        <FormError>{formError}</FormError>
        <Footer onClose={onClose} loading={m.isPending} label={ticket ? 'Сохранить' : 'Создать заявку'} />
      </form>
    </Dialog>
  );
}
